import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

interface PortalUser {
  id: string;
  user_id: string;
  customer_id: string;
  company_id: string;
  is_active: boolean;
  last_login_at: string | null;
}

interface Customer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  document_number: string | null;
}

interface Company {
  id: string;
  name: string;
  logo_url: string | null;
}

interface PortalSettings {
  portal_name: string;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
  welcome_message: string | null;
  show_prices: boolean;
  allow_orders: boolean;
  allow_loyalty: boolean;
}

interface PortalAuthContextType {
  user: User | null;
  session: Session | null;
  portalUser: PortalUser | null;
  customer: Customer | null;
  company: Company | null;
  settings: PortalSettings | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshPortalData: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

export const PortalAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [settings, setSettings] = useState<PortalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPortalData = async (userId: string) => {
    try {
      // Fetch portal user
      const { data: portalUserData, error: portalUserError } = await supabase
        .from('portal_users')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .single();

      if (portalUserError || !portalUserData) {
        setError('Usuário não tem acesso ao portal');
        return false;
      }

      setPortalUser(portalUserData);

      // Fetch customer data
      const { data: customerData } = await supabase
        .from('customers')
        .select('id, name, email, phone, document_number')
        .eq('id', portalUserData.customer_id)
        .single();

      if (customerData) {
        setCustomer(customerData);
      }

      // Fetch company data
      const { data: companyData } = await supabase
        .from('companies')
        .select('id, name, logo_url')
        .eq('id', portalUserData.company_id)
        .single();

      if (companyData) {
        setCompany(companyData);
      }

      // Fetch portal settings
      const { data: settingsData } = await supabase
        .from('portal_settings')
        .select('*')
        .eq('company_id', portalUserData.company_id)
        .single();

      if (settingsData) {
        setSettings({
          portal_name: settingsData.portal_name || 'Portal do Cliente',
          primary_color: settingsData.primary_color || '#3B82F6',
          secondary_color: settingsData.secondary_color || '#10B981',
          logo_url: settingsData.logo_url,
          welcome_message: settingsData.welcome_message,
          show_prices: settingsData.show_prices ?? true,
          allow_orders: settingsData.allow_orders ?? false,
          allow_loyalty: settingsData.allow_loyalty ?? false,
        });
      } else {
        // Default settings
        setSettings({
          portal_name: 'Portal do Cliente',
          primary_color: '#3B82F6',
          secondary_color: '#10B981',
          logo_url: null,
          welcome_message: 'Bem-vindo ao Portal do Cliente',
          show_prices: true,
          allow_orders: false,
          allow_loyalty: false,
        });
      }

      // Update last login
      await supabase
        .from('portal_users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', portalUserData.id);

      return true;
    } catch (err) {
      console.error('Error fetching portal data:', err);
      setError('Erro ao carregar dados do portal');
      return false;
    }
  };

  const refreshPortalData = async () => {
    if (user?.id) {
      await fetchPortalData(user.id);
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchPortalData(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (event === 'SIGNED_IN' && session?.user) {
          await fetchPortalData(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          setPortalUser(null);
          setCustomer(null);
          setCompany(null);
          setSettings(null);
          setError(null);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError(authError.message);
        return { success: false, error: authError.message };
      }

      if (data.user) {
        const hasAccess = await fetchPortalData(data.user.id);
        if (!hasAccess) {
          await supabase.auth.signOut();
          return { success: false, error: 'Usuário não tem acesso ao portal' };
        }
      }

      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao fazer login';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setPortalUser(null);
    setCustomer(null);
    setCompany(null);
    setSettings(null);
    setError(null);
  };

  return (
    <PortalAuthContext.Provider
      value={{
        user,
        session,
        portalUser,
        customer,
        company,
        settings,
        loading,
        error,
        signIn,
        signOut,
        refreshPortalData,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
};

export const usePortalAuth = () => {
  const context = useContext(PortalAuthContext);
  if (context === undefined) {
    throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  }
  return context;
};
